import {
  roundToTwo,
  formatUsd as formatAiRawAmount,
  parseCurrency as parseCurrencyValue,
  AI_ANALYSIS_KEYS,
  canonicalizeAiKey,
  extractAiValue,
  normalizeListings,
  nowIso,
} from '@lotlingo/shared';
import type { EstimateOverride, Lot, PriceReview, UserOverlay } from '../types';
import type { PriceReviewMetadata } from '../../types/api';

type UnknownRecord = Record<string, unknown>;

type Candidate = unknown;

export interface NormalizedAiCurrency {
  amount: number;
  raw: string;
  currency?: string;
}

export interface NormalizedPriceReview extends PriceReview {
  updatedAt?: string;
  reviewerPortalName?: string;
  reviewerPortalEmail?: string;
  reviewerPortalUserId?: string;
  reviewerUserId?: string;
  // Keep retries type aligned with PriceReviewMetadata definition (PriceRetryMetadata)
  retries?: import('../../types/api').PriceRetryMetadata;
}

// Re-export AI analysis keys for backwards compatibility
export { AI_ANALYSIS_KEYS };

export { normalizeListings };

// Alias for backwards compatibility
export { normalizeListings as normalizeAiListings };

// Type guard for objects
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export { canonicalizeAiKey, extractAiValue };

export function normalizeAiCurrency(candidate: unknown): NormalizedAiCurrency | undefined {
  if (candidate == null) {
    return undefined;
  }
  if (typeof candidate === 'number' && Number.isFinite(candidate)) {
    const normalizedAmount = roundToTwo(candidate);
    return { amount: normalizedAmount, raw: formatAiRawAmount(normalizedAmount) || '' };
  }
  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    if (!trimmed) {
      return undefined;
    }
    const parsed = parseCurrencyValue(trimmed);
    if (parsed == null) {
      return undefined;
    }
    const normalizedAmount = roundToTwo(parsed);
    const hasCurrencySymbol = /[$€£¥]/.test(trimmed);
    const raw = hasCurrencySymbol ? trimmed : formatAiRawAmount(normalizedAmount) || '';
    return { amount: normalizedAmount, raw };
  }
  if (Array.isArray(candidate)) {
    for (let i = 0; i < candidate.length; i += 1) {
      const normalized = normalizeAiCurrency(candidate[i]);
      if (normalized) {
        return normalized;
      }
    }
    return undefined;
  }
  if (isObject(candidate)) {
    const amountFields = ['amount', 'value', 'estimate', 'high', 'low', 'min', 'max'];
    for (let i = 0; i < amountFields.length; i += 1) {
      const key = amountFields[i];
      if (Object.prototype.hasOwnProperty.call(candidate, key)) {
        const normalized = normalizeAiCurrency(candidate[key]);
        if (normalized) {
          if (!normalized.raw && typeof candidate.raw === 'string' && candidate.raw.trim()) {
            normalized.raw = candidate.raw.trim() || '';
          }
          return normalized;
        }
      }
    }
    if (typeof candidate.raw === 'string') {
      const normalizedRaw = normalizeAiCurrency(candidate.raw);
      if (normalizedRaw) {
        return normalizedRaw;
      }
    }
    const values = Object.values(candidate);
    for (let i = 0; i < values.length; i += 1) {
      const normalized = normalizeAiCurrency(values[i]);
      if (normalized) {
        return normalized;
      }
    }
  }
  return undefined;
}

export function extractAiCurrency(
  analysis: unknown,
  keys: readonly string[]
): NormalizedAiCurrency | undefined {
  const candidate = extractAiValue(analysis, keys);
  return normalizeAiCurrency(candidate);
}

export function extractAiListings(
  analysis: unknown,
  keys: readonly string[]
): string[] | undefined {
  const candidate = extractAiValue(analysis, keys);
  return normalizeListings(candidate);
}

export function extractAiStatus(analysis: unknown, keys: readonly string[]): string | undefined {
  const candidate = extractAiValue(analysis, keys);
  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    return trimmed || undefined;
  }
  return undefined;
}

export function mapPriceReview(review: unknown): NormalizedPriceReview | undefined {
  if (!isObject(review)) {
    return undefined;
  }
  const normalized: NormalizedPriceReview = {};
  if (typeof review.status === 'string') {
    const statusVal = review.status as string;
    // Narrow status to valid enum type
    if (statusVal === 'failedPrice' || statusVal === 'successPrice') {
      normalized.status = statusVal;
    }
  }
  if (typeof review.updatedAt === 'string') {
    normalized.updatedAt = review.updatedAt;
  }
  if (typeof review.reviewerPortalName === 'string') {
    normalized.reviewerPortalName = review.reviewerPortalName;
  }
  if (typeof review.reviewerPortalEmail === 'string') {
    normalized.reviewerPortalEmail = review.reviewerPortalEmail;
  }
  if (typeof review.reviewerPortalUserId === 'string') {
    normalized.reviewerPortalUserId = review.reviewerPortalUserId;
  }
  if (typeof review.reviewerUserId === 'string') {
    normalized.reviewerUserId = review.reviewerUserId;
  }
  const retries = mapPriceRetry(review.retries);
  if (retries) {
    normalized.retries = retries;
  }
  return Object.keys(normalized).length ? normalized : undefined;
}

function mapPriceRetry(retries: unknown): import('../../types/api').PriceRetryMetadata | undefined {
  if (!isObject(retries)) {
    return undefined;
  }
  const countValue =
    typeof (retries as any).count === 'number'
      ? (retries as any).count
      : Number((retries as any).count);
  const lastAttemptedAt =
    typeof (retries as any).lastAttemptedAt === 'string' ? (retries as any).lastAttemptedAt : '';
  const lastAttemptedBy =
    typeof (retries as any).lastAttemptedBy === 'string' ? (retries as any).lastAttemptedBy : '';
  if (!Number.isFinite(countValue) || !lastAttemptedAt || !lastAttemptedBy) {
    return undefined; // Require core fields for a valid retry metadata
  }
  const meta: import('../../types/api').PriceRetryMetadata = {
    count: countValue,
    lastAttemptedAt,
    lastAttemptedBy,
    lastPortalUserId:
      typeof (retries as any).lastPortalUserId === 'string'
        ? (retries as any).lastPortalUserId
        : undefined,
    lastPortalUserEmail:
      typeof (retries as any).lastPortalUserEmail === 'string'
        ? (retries as any).lastPortalUserEmail
        : undefined,
    lastPortalUserName:
      typeof (retries as any).lastPortalUserName === 'string'
        ? (retries as any).lastPortalUserName
        : undefined,
  };
  return meta;
}

export function truncateText(value: unknown, limit?: number): string {
  const text = value == null ? '' : String(value);
  if (!limit || text.length <= limit) {
    return text;
  }
  if (limit <= 1) {
    return text.charAt(0);
  }
  return text.slice(0, limit - 1) + '…';
}

export function uid(): string {
  const globalCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
    return globalCrypto.randomUUID();
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

interface SharedLotPayload {
  url: string;
  source: string;
  title?: string;
  lotNumber?: string;
  itemNumber?: string;
  auctionTitle?: string;
  auctionId?: string;
  currentBid?: EstimateOverride;
  nextRequiredBid?: EstimateOverride;
  highSellValue?: EstimateOverride;
  lowSellValue?: EstimateOverride;
  highListings?: string[];
  lowListings?: string[];
  priceStatus?: string;
  location?: string;
  shipping?: string;
  closeDateTime?: { raw: string; iso: string };
  auctioneer?: string;
  auctioneerLink?: string;
  auctioneerLogoUrl?: string;
  description?: string;
  images?: string[];
}

interface SharedLot {
  id: string;
  itemNumber: string;
  payload: SharedLotPayload;
  payloadHash: string;
  aiAnalysis?: unknown;
  aiStatus?: string;
  aiAttempts?: number;
  aiError?: string;
  aiRequestedAt?: string;
  aiCompletedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface LocalUserOverlay {
  id: string;
  userId: string;
  lotId: string;
  itemNumber: string;
  auctionId?: string;
  source?: string;
  payloadHash: string;
  status: string;
  favorite: boolean;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
  // Additional overlay properties
  highSellOverride?: EstimateOverride | null;
  lowSellOverride?: EstimateOverride | null;
  priceReview?: PriceReviewMetadata;
  estimateOverride?: EstimateOverride;
}

type ExtendedLot = Lot & {
  sharedLot?: SharedLot;
  // Maintain enriched LocalUserOverlay; plain UserOverlay will be upgraded below
  userOverlay?: LocalUserOverlay;
  currentBid?: number;
  nextRequiredBid?: number;
  highListings?: string[];
  lowListings?: string[];
  lotNumber?: string;
  itemNumber?: string;
  auctionTitle?: string;
  auctionId?: string;
  location?: string;
  shipping?: string;
  closeDateTime?: string;
  auctioneer?: string;
  auctioneerLink?: string;
  auctioneerLogoUrl?: string;
  notes?: string;
  images?: string[];
  imageUrl?: string;
  status?: string;
  url?: string;
  source?: string;
  favorite?: boolean;
  createdAt?: string | number;
  updatedAt?: string | number;
  aiAttempts?: number;
  aiRequestedAt?: string;
  aiCompletedAt?: string;
};

export function ensureLocalStructures(
  input?: Partial<ExtendedLot> | Partial<Lot> | null
): ExtendedLot {
  const lot = (input || ({} as ExtendedLot)) as ExtendedLot;
  lot.id = lot.id || uid();
  lot.remote = false;
  lot.favorite = !!lot.favorite;
  lot.deleted = !!lot.deleted;

  const shouldBuildSharedLot =
    !lot.sharedLot ||
    typeof lot.sharedLot !== 'object' ||
    !lot.sharedLot.payload ||
    typeof lot.sharedLot.payload !== 'object';

  if (shouldBuildSharedLot) {
    const sharedCreatedIso = lot.createdAt != null ? toIsoString(lot.createdAt) : nowIso();
    const sharedUpdatedIso = lot.updatedAt != null ? toIsoString(lot.updatedAt) : sharedCreatedIso;
    const payload: SharedLotPayload = {
      url: typeof lot.url === 'string' ? lot.url : '',
      source: lot.source || 'generic',
    };

    if (typeof lot.title === 'string' && lot.title) {
      payload.title = lot.title;
    }
    if (typeof lot.lotNumber === 'string' && lot.lotNumber) {
      payload.lotNumber = lot.lotNumber;
    }
    if (typeof lot.itemNumber === 'string' && lot.itemNumber) {
      payload.itemNumber = lot.itemNumber;
    }
    if (typeof lot.auctionTitle === 'string' && lot.auctionTitle) {
      payload.auctionTitle = lot.auctionTitle;
    }
    if (typeof lot.auctionId === 'string' && lot.auctionId) {
      payload.auctionId = lot.auctionId;
    }
    if (typeof lot.currentBid === 'number' && Number.isFinite(lot.currentBid)) {
      const bidRounded = roundToTwo(lot.currentBid);
      payload.currentBid = { amount: bidRounded, raw: bidRounded.toFixed(2) };
    }
    if (typeof lot.nextRequiredBid === 'number' && Number.isFinite(lot.nextRequiredBid)) {
      const nextRounded = roundToTwo(lot.nextRequiredBid);
      payload.nextRequiredBid = { amount: nextRounded, raw: nextRounded.toFixed(2) };
    }
    const highCurrency = currencyFromAmount(lot.highSellValue);
    if (highCurrency) {
      payload.highSellValue = highCurrency;
    }
    const lowCurrency = currencyFromAmount(lot.lowSellValue);
    if (lowCurrency) {
      payload.lowSellValue = lowCurrency;
    }
    const normalizedHighListings = normalizeListings(lot.highListings);
    if (normalizedHighListings?.length) {
      payload.highListings = normalizedHighListings;
    }
    const normalizedLowListings = normalizeListings(lot.lowListings);
    if (normalizedLowListings?.length) {
      payload.lowListings = normalizedLowListings;
    }
    if (typeof lot.priceStatus === 'string' && lot.priceStatus) {
      payload.priceStatus = lot.priceStatus;
    }
    if (typeof lot.location === 'string' && lot.location) {
      payload.location = lot.location;
    }
    if (typeof lot.shipping === 'string' && lot.shipping) {
      payload.shipping = lot.shipping;
    }
    if (typeof lot.closeDateTime === 'string' && lot.closeDateTime) {
      payload.closeDateTime = { raw: lot.closeDateTime, iso: lot.closeDateTime };
    }
    if (typeof lot.auctioneer === 'string' && lot.auctioneer) {
      payload.auctioneer = lot.auctioneer;
    }
    if (typeof lot.auctioneerLink === 'string' && lot.auctioneerLink) {
      payload.auctioneerLink = lot.auctioneerLink;
    }
    if (typeof lot.auctioneerLogoUrl === 'string' && lot.auctioneerLogoUrl) {
      payload.auctioneerLogoUrl = lot.auctioneerLogoUrl;
    }
    const images = Array.isArray(lot.images)
      ? lot.images.slice()
      : typeof lot.imageUrl === 'string' && lot.imageUrl
        ? [lot.imageUrl]
        : [];
    if (images.length) {
      payload.images = images;
    }
    if (typeof lot.notes === 'string' && lot.notes) {
      payload.description = lot.notes;
    }
    lot.sharedLot = {
      id: lot.id,
      itemNumber: lot.itemNumber || lot.lotNumber || lot.id,
      payload,
      payloadHash: typeof lot.sharedLot?.payloadHash === 'string' ? lot.sharedLot.payloadHash : '',
      aiAnalysis: isObject(lot.aiAnalysis) ? lot.aiAnalysis : undefined,
      aiStatus: typeof lot.aiStatus === 'string' ? lot.aiStatus : undefined,
      aiAttempts: typeof lot.aiAttempts === 'number' ? lot.aiAttempts : undefined,
      aiError: typeof lot.aiError === 'string' ? lot.aiError : undefined,
      aiRequestedAt: typeof lot.aiRequestedAt === 'string' ? lot.aiRequestedAt : undefined,
      aiCompletedAt: typeof lot.aiCompletedAt === 'string' ? lot.aiCompletedAt : undefined,
      createdAt: sharedCreatedIso,
      updatedAt: sharedUpdatedIso,
    };
  }

  const shouldBuildUserOverlay =
    !lot.userOverlay || typeof lot.userOverlay !== 'object' || !(lot.userOverlay as any).id;

  if (shouldBuildUserOverlay) {
    const overlayCreatedIso = lot.createdAt != null ? toIsoString(lot.createdAt) : nowIso();
    const overlayUpdatedIso =
      lot.updatedAt != null ? toIsoString(lot.updatedAt) : overlayCreatedIso;
    lot.userOverlay = {
      id: (lot.userOverlay as any)?.id || uid(),
      userId: 'local-offline',
      lotId: lot.id || lot.sharedLot?.id || uid(),
      itemNumber: lot.itemNumber || lot.lotNumber || lot.sharedLot?.itemNumber || lot.id || uid(),
      auctionId: lot.auctionId || undefined,
      source: lot.source || undefined,
      payloadHash: lot.sharedLot?.payloadHash || '',
      status: typeof lot.status === 'string' ? lot.status : 'received',
      favorite: !!lot.favorite,
      deleted: !!lot.deleted,
      highSellOverride: currencyFromAmount(lot.highSellValue) || undefined,
      lowSellOverride: currencyFromAmount(lot.lowSellValue) || undefined,
      priceReview: lot.priceReview ? JSON.parse(JSON.stringify(lot.priceReview)) : undefined,
      createdAt: overlayCreatedIso,
      updatedAt: overlayUpdatedIso,
    };
  }

  return lot;
}

function currencyFromAmount(amount: number | undefined): EstimateOverride | undefined {
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    const rounded = roundToTwo(amount);
    return { amount: rounded, raw: rounded.toFixed(2), currency: 'USD' };
  }
  return undefined;
}

function toIsoString(value: string | number): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) {
      return new Date(parsed).toISOString();
    }
  }
  return nowIso();
}

// Removed duplicate isObject definition at file end (already defined earlier)
